const calculateNetRecurringBenefitsWithInitiativesByRange = (initiatives) => {
  const l0ToL2 = ['L0', 'L1', 'L2', 'Submitted for L1 Approval', 'Submitted for L2 Approval', 'Submitted for L3 Approval'];
  const L3ToL5 = ['L3', 'L4', 'L5', 'Submitted for L4 Approval', 'Submitted for L5 Approval'];

  // Helper to get the single numerical plan or actual value
  const getImpactValue = (impact, type) => {
      if (!impact || !impact.annualizedValues) {
          return 0;
      }
      const value = impact.annualizedValues[type]; // 'plan' or 'actual'
      return typeof value === 'number' ? value : 0;
  };

  const calculateTotalRecurringBenefit = (initiative) => {
      const impacts = initiative.impact || [];

      if (!impacts || impacts.length === 0) {
          return NaN; // Indicate no impact
      }

      let recurringBenefit = 0;
      let negativeRecurringImpact = 0;

      // Use the determineStage function if available, or fall back to initiative.stage
      // Assuming determineStage takes an initiative object and returns a single string stage
      const currentStage = initiative.stage; // Using initiative.stage directly as per provided logic context

      if (currentStage) {
          if (l0ToL2.includes(currentStage)) {
              recurringBenefit = impacts
                  .filter((impact) => impact.frequencyAndDirection === 'Recurring benefits')
                  .reduce((sum, impact) => sum + getImpactValue(impact, 'plan'), 0);

              negativeRecurringImpact = impacts
                  .filter((impact) => impact.frequencyAndDirection === 'Recurring Negative Impact (ETB)')
                  .reduce((sum, impact) => sum + getImpactValue(impact, 'plan'), 0);
          } else if (L3ToL5.includes(currentStage)) {
              recurringBenefit = impacts
                  .filter((impact) => impact.frequencyAndDirection === 'Recurring benefits')
                  .reduce((sum, impact) => sum + getImpactValue(impact, 'actual'), 0);

              negativeRecurringImpact = impacts
                  .filter((impact) => impact.frequencyAndDirection === 'Recurring Negative Impact (ETB)')
                  .reduce((sum, impact) => sum + getImpactValue(impact, 'actual'), 0);
          }
      }

      return recurringBenefit - negativeRecurringImpact;
  };

  const now = new Date();
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const rangeInitiativesMap = {
      '<not set>': [],
      'Increased by > 1 ETB m': [],
      'Increased by 0.1-1 ETB m': [],
      'Increased by < 0.1 ETB m': [],
      'Unchanged': [],
      'Decreased by < 0.1 ETB m': [],
      'Decreased by 0.1-1 ETB m': [],
      'Decreased by > 1 ETB m': [],
  };

  initiatives.forEach((initiative) => {
      const history = initiative.history || [];
      const impactUpdateHistory = history.filter(
          (entry) =>
              entry.action &&
              entry.action.includes('Impact number') &&
              entry.action.includes('has been updated.')
      );

      const oldImpactHistories = impactUpdateHistory.filter((entry) => new Date(entry.timestamp) < oneWeekAgo);

      oldImpactHistories.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      let lastKnownNewAnnualizedPlan = 0;
      let lastKnownPreviousAnnualizedPlan = 0;

      // Iterate through oldImpactHistories to find the last recorded 'new' and 'previous' annualized plan values
      // for recurring benefits/impacts before 'oneWeekAgo'.
      for (let i = 0; i < oldImpactHistories.length; i++) {
          const entry = oldImpactHistories[i];
          if (
              entry.changes &&
              entry.changes.impact &&
              entry.changes.impact.new &&
              typeof entry.changes.impact.new.annualizedValues?.plan === 'number' &&
              entry.changes.impact.previous &&
              typeof entry.changes.impact.previous.annualizedValues?.plan === 'number'
          ) {
              const newPlanValue = entry.changes.impact.new.annualizedValues.plan;
              const previousPlanValue = entry.changes.impact.previous.annualizedValues.plan;

              if (entry.action.includes('Recurring benefits')) {
                  lastKnownNewAnnualizedPlan += newPlanValue;
                  lastKnownPreviousAnnualizedPlan += previousPlanValue;
                  break; // Found the most recent, so stop
              } else if (entry.action.includes('Recurring Negative Impact (ETB)')) {
                  lastKnownNewAnnualizedPlan -= newPlanValue;
                  lastKnownPreviousAnnualizedPlan -= previousPlanValue;
                  break; // Found the most recent, so stop
              }
          }
      }

      const currentValue = calculateTotalRecurringBenefit(initiative);
      const lastWeekValue = lastKnownNewAnnualizedPlan; // This now accurately represents the 'new' value from the last relevant history entry

      let range = '<not set>';

      if (isNaN(currentValue)) {
          range = '<not set>'; // Case of no Impact
      } else if (currentValue === lastWeekValue) {
          range = 'Unchanged'; // Case current equals last week
      } else {
          const difference = currentValue - lastWeekValue;

          if (difference > 1000000) {
              range = 'Increased by > 1 ETB m';
          } else if (difference >= 100000 && difference <= 1000000) {
              range = 'Increased by 0.1-1 ETB m';
          } else if (difference > 0 && difference < 100000) {
              range = 'Increased by < 0.1 ETB m';
          } else if (difference < 0 && difference > -100000) {
              range = 'Decreased by < 0.1 ETB m';
          } else if (difference <= -100000 && difference >= -1000000) {
              range = 'Decreased by 0.1-1 ETB m';
          } else if (difference < -1000000) {
              range = 'Decreased by > 1 ETB m';
          }
      }

      rangeInitiativesMap[range].push(initiative);
  });

  return rangeInitiativesMap;
};

export default calculateNetRecurringBenefitsWithInitiativesByRange;